import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  FormControlLabel, 
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * Dialog per la pubblicazione della verifica
 */
const PublishDialog = ({
  open,
  onClose,
  publishTitle,
  onChangeTitle,
  description,
  onChangeDescription,
  activityType,
  onChangeActivityType,
  onPublish,
  publishing,
  publishedGameId
}) => {
  // Copia l'URL del gioco pubblicato negli appunti
  const handleCopyLink = () => {
    if (publishedGameId) {
      navigator.clipboard.writeText(`${window.location.origin}/games/${publishedGameId}`);
      // Nota: lo snackbar viene gestito dal componente principale
    }
  };

  // Apre l'attività in una nuova tab/finestra
  const handleOpenActivity = () => {
    if (publishedGameId) {
      window.open(`${window.location.origin}/games/${publishedGameId}`, '_blank');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '1rem',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f9f9f9',
        borderBottom: '1px solid #e0e0e0',
        padding: '1.5rem',
        fontWeight: 'bold'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PublishIcon sx={{ color: '#df4634' }} />
          <Typography variant="h6">Pubblica Attività Interattiva</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ padding: '2rem' }}>
        {!publishedGameId ? (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Quando pubblichi un'attività, essa diventa accessibile tramite un link univoco che puoi condividere con gli studenti o colleghi.
            </Typography>
            
            <TextField
              label="Titolo dell'attività"
              value={publishTitle}
              onChange={(e) => onChangeTitle(e.target.value)}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              autoFocus
              InputProps={{
                sx: { borderRadius: '0.5rem' }
              }}
            />
            
            <TextField
              label="Descrizione dell'attività"
              value={description}
              onChange={(e) => onChangeDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              margin="normal"
              variant="outlined"
              placeholder="Inserisci una breve descrizione dell'attività (opzionale)"
              InputProps={{
                sx: { borderRadius: '0.5rem' }
              }}
            />
            
            <Box sx={{ mt: 3 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500, color: '#df4634' }}>
                  Tipo di attività
                </FormLabel>
                <RadioGroup
                  row
                  value={activityType}
                  onChange={(e) => onChangeActivityType(e.target.value)}
                >
                  <FormControlLabel 
                    value="verifica" 
                    control={<Radio sx={{ 
                      color: '#df4634',
                      '&.Mui-checked': {
                        color: '#df4634',
                      }
                    }} />} 
                    label="Verifica" 
                    sx={{ mr: 3 }}
                  />
                  <FormControlLabel 
                    value="esercitazione" 
                    control={<Radio sx={{ 
                      color: '#df4634',
                      '&.Mui-checked': {
                        color: '#df4634',
                      }
                    }} />} 
                    label="Esercitazione" 
                  />
                </RadioGroup>
              </FormControl>
              
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: 'rgba(223,70,52,0.05)', 
                borderRadius: '0.75rem',
                border: '1px solid rgba(223,70,52,0.1)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5
              }}>
                <InfoOutlinedIcon sx={{ color: '#df4634', mt: 0.3, fontSize: '1.1rem' }} />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {activityType === 'verifica' ? (
                      'La verifica non mostrerà il punteggio finale agli studenti'
                    ) : (
                      'L\'esercitazione mostrerà il punteggio finale agli studenti'
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    {activityType === 'verifica' ? (
                      'Utilizzala per valutazioni formali in cui non vuoi che gli studenti vedano immediatamente il loro risultato finale.'
                    ) : (
                      'Utilizzala per esercizi di allenamento in cui gli studenti possono monitorare autonomamente il loro progresso.'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ 
              backgroundColor: 'rgba(223,70,52,0.05)', 
              borderRadius: '0.75rem', 
              padding: '1rem',
              mt: 3,
              border: '1px solid rgba(223,70,52,0.1)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2
            }}>
              <Box sx={{ color: '#df4634', mt: 0.5 }}>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                </svg>
              </Box>
              <Typography variant="body2">
                La pubblicazione include tutte le attività presenti. Gli studenti potranno svolgere le attività ma non modificarle.
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
              <Button 
                onClick={onClose} 
                variant="outlined"
                sx={{ 
                  borderColor: '#df4634',
                  color: '#df4634',
                  '&:hover': {
                    borderColor: '#c13c2b',
                    backgroundColor: 'rgba(223,70,52,0.04)'
                  }
                }}
              >
                Annulla
              </Button>
              <Button 
                onClick={onPublish} 
                variant="contained" 
                sx={{
                  backgroundColor: '#df4634',
                  '&:hover': {
                    backgroundColor: '#c13c2b'
                  }
                }}
                disabled={!publishTitle.trim() || publishing}
                startIcon={publishing ? <CircularProgress size={20} color="inherit" /> : <PublishIcon />}
              >
                {publishing ? 'Pubblicazione...' : 'Pubblica'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ 
              backgroundColor: 'rgba(223,70,52,0.1)',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem auto'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#df4634"/>
              </svg>
            </Box>
            
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              {activityType === 'verifica' ? 'Verifica' : 'Esercitazione'} pubblicata con successo!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              La tua {activityType === 'verifica' ? 'verifica' : 'esercitazione'} è ora disponibile al seguente link:
            </Typography>
            
            <TextField
              value={`${window.location.origin}/games/${publishedGameId}`}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                sx: { 
                  borderRadius: '0.5rem',
                  fontFamily: 'monospace',
                  backgroundColor: '#f9f9f9'
                }
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
              <Button 
                onClick={handleCopyLink}
                variant="contained"
                startIcon={<ContentCopyIcon />}
                sx={{ 
                  borderRadius: '1.5rem',
                  backgroundColor: '#df4634',
                  '&:hover': {
                    backgroundColor: '#c13c2b'
                  }
                }}
              >
                Copia Link
              </Button>
              
              <Button 
                onClick={handleOpenActivity}
                variant="contained"
                startIcon={<LaunchIcon />}
                sx={{ 
                  borderRadius: '1.5rem',
                  backgroundColor: '#2e7d32',
                  '&:hover': {
                    backgroundColor: '#1b5e20'
                  }
                }}
              >
                Apri Attività
              </Button>
              
              <Button 
                onClick={onClose}
                variant="outlined"
                sx={{ 
                  borderRadius: '1.5rem',
                  borderColor: '#df4634',
                  color: '#df4634',
                  '&:hover': {
                    borderColor: '#c13c2b',
                    backgroundColor: 'rgba(223,70,52,0.04)'
                  }
                }}
              >
                Chiudi
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublishDialog; 